import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiPackage,
  FiClock,
  FiMapPin,
  FiUser,
  FiTruck,
  FiImage,
  FiX,
  FiCheckCircle,
  FiRefreshCw,
  FiXCircle,
  FiShoppingBag,
  FiDollarSign,
  FiCreditCard,
  FiArrowLeft,
  FiTrendingUp,
  FiCalendar,
} from 'react-icons/fi';
import API, { getSchemaName, getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';
import { useAuth } from 'shared/context/AuthContext';
import socket from 'shared/socket';
import HistorialFiltros from './HistorialFiltros';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const ESTADOS_META = {
  pendiente: { label: 'Pendiente', color: '#fbbc04' },
  en_revision: { label: 'En revisión', color: '#FF9500' },
  aprobado: { label: 'Aprobado', color: '#0f9d58' },
  esperando_pago: { label: 'Esperando pago', color: '#9c27b0' },
  pago_en_verificacion: { label: 'Verif. pago', color: '#673ab7' },
  pagado: { label: 'Pagado', color: '#0f9d58' },
  armado: { label: 'Armado', color: '#ff6d00' },
  armado_cocina: { label: 'En cocina', color: '#ff6d00' },
  listo: { label: 'Listo', color: '#1a73e8' },
  despachado: { label: 'Despachado', color: '#00bcd4' },
  en_camino: { label: 'En camino', color: '#00bcd4' },
  llegada: { label: 'Llegada', color: '#009688' },
  entregado: { label: 'Entregado', color: '#34a853' },
  cancelado: { label: 'Cancelado', color: '#9e9e9e' },
  rechazado: { label: 'Rechazado', color: '#ea4335' },
  credito_pagado: { label: 'Crédito pagado', color: '#34a853' },
};

const ESTADOS_FINALES = ['entregado', 'credito_pagado', 'cancelado', 'rechazado'];

const TIPO_ENTREGA = {
  delivery: { label: 'Delivery', icon: FiTruck, color: '#00bcd4' },
  retiro: { label: 'Retiro', icon: FiShoppingBag, color: '#1a73e8' },
  encomienda: { label: 'Encomienda', icon: FiTruck, color: '#009688' },
};

const formatearFecha = (f) => {
  if (!f) return '';
  const d = new Date(f);
  return (
    d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  );
};

const fmt = (n) => `$${fmtMonto(n)}`;

const localISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const PedidosHistorial = ({ sedeFilter }) => {
  const { tienePermiso } = useAuth();
  const puedeAnular = tienePermiso('pedidos.gestionar');
  const [pedidos, setPedidos] = useState([]);
  const [resumen, setResumen] = useState({
    count_total: 0, count_entregado: 0, count_cancelado: 0,
    total_facturado: 0, ticket_promedio: 0,
  });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [orden, setOrden] = useState('reciente');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaDebounced, setBusquedaDebounced] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [comprobante, setComprobante] = useState(null);
  const [confirmar, setConfirmar] = useState(null);

  const busquedaTimer = useRef(null);
  useEffect(() => {
    if (busquedaTimer.current) clearTimeout(busquedaTimer.current);
    busquedaTimer.current = setTimeout(() => setBusquedaDebounced(busqueda.trim()), 300);
    return () => { if (busquedaTimer.current) clearTimeout(busquedaTimer.current); };
  }, [busqueda]);

  const hayFiltrosActivos =
    !!filtroEstado || !!filtroTipo || !!filtroFecha || !!fechaDesde || !!fechaHasta || !!filtroMetodo || !!busquedaDebounced || orden !== 'reciente';

  const limpiarFiltros = () => {
    setFiltroEstado('');
    setFiltroTipo('');
    setFiltroFecha('');
    setFechaDesde('');
    setFechaHasta('');
    setFiltroMetodo('');
    setOrden('reciente');
    setBusqueda('');
    setBusquedaDebounced('');
  };

  const buildParams = useCallback((extra) => {
    const p = new URLSearchParams();
    p.append('limite', '30');
    p.append('tipo_pedido', 'online');
    p.append('tipo_pedido', 'delivery');
    if (sedeFilter) p.append('codigo_sede', sedeFilter);
    if (filtroEstado) p.append('estado', filtroEstado);
    if (filtroTipo) p.append('tipo_entrega', filtroTipo);
    if (busquedaDebounced) p.append('q', busquedaDebounced);
    if (filtroMetodo) p.append('metodo_pago', filtroMetodo);
    if (filtroFecha) {
      const now = new Date();
      if (filtroFecha === 'hoy') {
        p.append('desde', localISO(now));
        p.append('hasta', localISO(now));
      } else if (filtroFecha === 'ayer') {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        p.append('desde', localISO(y));
        p.append('hasta', localISO(y));
      } else if (filtroFecha === '7dias') {
        const y = new Date(now); y.setDate(y.getDate() - 6);
        p.append('desde', localISO(y));
        p.append('hasta', localISO(now));
      } else if (filtroFecha === '30dias') {
        const y = new Date(now); y.setDate(y.getDate() - 29);
        p.append('desde', localISO(y));
        p.append('hasta', localISO(now));
      } else if (filtroFecha === 'personalizado') {
        if (fechaDesde) p.append('desde', fechaDesde);
        if (fechaHasta) p.append('hasta', fechaHasta);
      }
    }
    if (extra?.ultimo_id) p.append('ultimo_id', extra.ultimo_id);
    return p;
  }, [sedeFilter, filtroEstado, filtroTipo, filtroFecha, fechaDesde, fechaHasta, filtroMetodo, busquedaDebounced]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await API.get(`/pedidos/listar/${getSchemaName()}?${buildParams().toString()}`);
      if (res.data.status === 'success') {
        setPedidos(res.data.data || []);
        setResumen(res.data.resumen || { count_total: 0, count_entregado: 0, count_cancelado: 0, total_facturado: 0, ticket_promedio: 0 });
        setHasMore(!!res.data.has_more);
      }
    } catch (e) {
      console.error('Error cargando historial:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    const handler = () => cargar();
    socket.on('pedido_actualizado', handler);
    return () => socket.off('pedido_actualizado', handler);
  }, [cargar]);

  const cargarMas = async () => {
    if (!pedidos.length || loadingMore) return;
    const last = pedidos[pedidos.length - 1];
    setLoadingMore(true);
    try {
      const res = await API.get(`/pedidos/listar/${getSchemaName()}?${buildParams({ ultimo_id: last.id }).toString()}`);
      const more = res.data?.data || [];
      setPedidos((prev) => [...prev, ...more]);
      setHasMore(!!res.data?.has_more);
    } catch {
      /* ignore */
    } finally {
      setLoadingMore(false);
    }
  };

  const totalPedido = (pd) => parseFloat(pd.total || 0) + parseFloat(pd.costo_envio || 0);

  const ordenados = useMemo(() => {
    const arr = [...pedidos];
    if (orden === 'reciente') {
      arr.sort((a, b) => new Date(b.fechapedido || 0) - new Date(a.fechapedido || 0));
    } else if (orden === 'antiguo') {
      arr.sort((a, b) => new Date(a.fechapedido || 0) - new Date(b.fechapedido || 0));
    } else if (orden === 'monto_mayor') {
      arr.sort((a, b) => totalPedido(b) - totalPedido(a));
    } else if (orden === 'monto_menor') {
      arr.sort((a, b) => totalPedido(a) - totalPedido(b));
    }
    return arr;
  }, [pedidos, orden]);

  const contarItems = (pd) =>
    (Array.isArray(pd.productos) ? pd.productos : []).reduce(
      (s, i) => s + (Number(i.cantidad) || 1),
      0
    );

  const abrirDetalle = async (pd) => {
    setDetalle(pd);
    setDetalleLoading(true);
    try {
      const [rDet, rHist] = await Promise.all([
        API.get(`/pedidos/${pd.id}`),
        API.get(`/pedidos/${pd.id}/historial`),
      ]);
      const full = rDet.data?.status === 'success' ? rDet.data.data : pd;
      const historial = rHist.data?.status === 'success' ? rHist.data.data : [];
      setDetalle({ ...full, _historial: historial });
    } catch (e) {
      console.error('Error cargando detalle:', e);
      setDetalle({ ...pd, _historial: [] });
    } finally {
      setDetalleLoading(false);
    }
  };

  const abrirDetalleRef = useRef(abrirDetalle);
  useEffect(() => { abrirDetalleRef.current = abrirDetalle; });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('agorax_pedido_abrir');
      if (!raw) return;
      localStorage.removeItem('agorax_pedido_abrir');
      const data = JSON.parse(raw);
      if (data && data.vista === 'online' && data.id) {
        abrirDetalleRef.current({ id: data.id });
      }
    } catch {}
  }, []);

  const anular = (pd) => {
    setConfirmar({
      title: 'Anular pedido',
      message: `El pedido #${pd.id} se marcará como cancelado y el inventario será devuelto.`,
      onConfirm: async () => {
        setConfirmar(null);
        try {
          await API.put(`/pedidos/${pd.id}/anular`, { motivo: 'Anulado desde historial' });
          setDetalle(null);
          cargar();
        } catch (e) {
          alert(e?.response?.data?.detail || 'No se pudo anular el pedido');
        }
      },
    });
  };

  const parseDatosPago = (dp) => {
    if (!dp) return null;
    if (typeof dp === 'object') return dp;
    try { return JSON.parse(dp); } catch { return null; }
  };

  const renderProductos = (productos) => {
    if (!Array.isArray(productos) || productos.length === 0) {
      return <div style={{ fontSize: 12, color: 'var(--agx-faint)', padding: '4px 0' }}>Sin productos</div>;
    }
    return productos.map((item, i) => (
      <div
        key={i}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: `1px solid var(--agx-border)` }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--agx-text)', wordBreak: 'break-word' }}>
            {item.nombre || item.codigo}
          </span>
          <span style={{ fontSize: 13, color: 'var(--agx-muted)', marginLeft: 6 }}>x{item.cantidad}</span>
          {(item.etiquetas || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 3 }}>
              {item.etiquetas.map((et, ei) => (
                <span key={ei} style={{ fontSize: 9, fontWeight: 600, background: 'var(--agx-amber-bg)', color: 'var(--agx-warning-text)', padding: '1px 5px', borderRadius: 3 }}>
                  {et.nombre}{et.precio > 0 ? ` +${fmt(et.precio)}` : ''}
                </span>
              ))}
            </div>
          )}
          {item.combo_id && Array.isArray(item.componentes) && item.componentes.length > 0 && (
            <div style={{ marginTop: 4, paddingLeft: 2 }}>
              {item.componentes.map((comp, ci) => {
                const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
                return (
                  <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--agx-muted)', padding: '1px 0' }}>
                    <span style={{ display: 'inline-block', width: 4, height: 4, borderRadius: '50%', backgroundColor: '#f0b400', flexShrink: 0 }} />
                    <span style={{ color: 'var(--agx-text)' }}>{comp.nombre}</span>
                    {compEtiq.length > 0 && <span style={{ color: 'var(--agx-faint)' }}>({compEtiq.map((e) => e.nombre).join(', ')})</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--agx-text)', flexShrink: 0 }}>
          {fmt((item.precio_unitario || item.precio || 0) * (item.cantidad || 1))}
        </span>
      </div>
    ));
  };

  const kpis = [
    { label: 'Total facturado', value: fmt(resumen.total_facturado), icon: FiDollarSign, color: 'var(--agx-green)' },
    { label: 'Entregados', value: resumen.count_entregado, icon: FiCheckCircle, color: 'var(--agx-green)' },
    { label: 'Cancelados', value: resumen.count_cancelado, icon: FiXCircle, color: 'var(--agx-red)' },
    { label: 'Ticket promedio', value: fmt(resumen.ticket_promedio), icon: FiTrendingUp, color: 'var(--agx-pri2)' },
  ];

  // Metodos de pago disponibles (derivados de los pedidos cargados).
  const metodos = useMemo(() => {
    const set = new Set();
    pedidos.forEach((pd) => {
      if (pd.metodo_pago) set.add(pd.metodo_pago);
      const dp = parseDatosPago(pd.datos_pago);
      if (dp?.metodo_nombre) set.add(dp.metodo_nombre);
      (Array.isArray(pd.pagos) ? pd.pagos : []).forEach((pg) => {
        const m = pg.metodo || pg.nombre;
        if (m) set.add(m);
      });
    });
    return Array.from(set).sort();
  }, [pedidos]);

  const estadoTabs = [
    { id: '', label: 'Todos' },
    { id: 'entregado', label: 'Entregados' },
    { id: 'cancelado', label: 'Cancelados' },
    { id: 'rechazado', label: 'Rechazados' },
  ];
  const tipoTabs = [
    { id: '', label: 'Todos' },
    { id: 'delivery', label: 'Delivery' },
    { id: 'retiro', label: 'Retiro' },
    { id: 'encomienda', label: 'Encomienda' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'var(--agx-bg)' }}>
      {/* Header + filtros */}
      <div style={{ padding: '12px 16px', backgroundColor: 'var(--agx-surface)', borderBottom: '1px solid var(--agx-border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--agx-text)' }}>Historial de Pedidos</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--agx-muted)', background: 'var(--agx-bg)', padding: '3px 10px', borderRadius: 999 }}>
              {resumen.count_total} en este filtro
            </span>
            <button onClick={cargar} title="Refrescar" style={{ border: 'none', background: 'var(--agx-bg)', borderRadius: 8, cursor: 'pointer', padding: 8, color: 'var(--agx-muted)', display: 'flex' }}>
              <FiRefreshCw size={15} />
            </button>
          </div>
        </div>

        <HistorialFiltros
          filtros={{
            estado: filtroEstado,
            tipo: filtroTipo,
            fecha: filtroFecha,
            desde: fechaDesde,
            hasta: fechaHasta,
            metodo: filtroMetodo,
            orden,
            busqueda,
          }}
          set={{
            estado: setFiltroEstado,
            tipo: setFiltroTipo,
            fecha: setFiltroFecha,
            desde: setFechaDesde,
            hasta: setFechaHasta,
            metodo: setFiltroMetodo,
            orden: setOrden,
            busqueda: setBusqueda,
          }}
          opciones={{ estados: estadoTabs, tipos: tipoTabs, metodos }}
          onLimpiar={limpiarFiltros}
          hayActivos={hayFiltrosActivos}
        />
      </div>

      {/* KPIs */}
      {!loading && !error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, padding: '12px 16px', flexShrink: 0 }}>
          {kpis.map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} style={{ background: 'var(--agx-surface)', borderRadius: 14, border: '1px solid var(--agx-border)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: isDark() ? k.color + '30' : k.color + '18', color: k.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={19} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--agx-muted)', fontWeight: 600 }}>{k.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--agx-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.value}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lista */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--agx-muted)' }}>Cargando...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--agx-muted)' }}>No se pudo cargar el historial.</div>
        ) : ordenados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--agx-faint)' }}>
            <FiPackage size={40} style={{ opacity: 0.25, marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {busquedaDebounced ? `Sin resultados para "${busquedaDebounced}"` : 'Sin pedidos'}
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Ajusta los filtros para ver más resultados</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {ordenados.map((pd) => {
                const meta = ESTADOS_META[pd.estado] || { label: pd.estado, color: '#9e9e9e' };
                const tipo = TIPO_ENTREGA[pd.tipo_entrega] || TIPO_ENTREGA.delivery;
                const TipoIcon = tipo.icon;
                const esFinal = ESTADOS_FINALES.includes(pd.estado);
                const itemsCount = contarItems(pd);
                return (
                  <div
                    key={pd.id}
                    onClick={() => abrirDetalle(pd)}
                    style={{
                      background: 'var(--agx-surface)',
                      borderRadius: 14,
                      border: '1px solid var(--agx-border)',
                      borderLeft: `5px solid ${meta.color}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ padding: '12px 14px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--agx-pri)' }}>#{pd.id}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: isDark() ? meta.color + '40' : meta.color + '18', color: meta.color }}>
                          {pd.estado === 'entregado' || pd.estado === 'credito_pagado' ? <FiCheckCircle size={11} /> : pd.estado === 'cancelado' || pd.estado === 'rechazado' ? <FiXCircle size={11} /> : <FiClock size={11} />}
                          {meta.label}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: isDark() ? tipo.color + '40' : tipo.color + '18', color: tipo.color }}>
                          <TipoIcon size={11} /> {tipo.label}
                        </span>
                        <span style={{ marginLeft: 'auto', fontSize: 17, fontWeight: 800, color: esFinal && pd.estado === 'cancelado' ? 'var(--agx-faint)' : 'var(--agx-text)' }}>
                          {fmt(parseFloat(pd.total || 0) + parseFloat(pd.costo_envio || 0))}
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: 'var(--agx-muted)' }}>
                        {pd.cliente && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiUser size={12} /> {pd.cliente}</span>
                        )}
                        {pd.tipo_pedido === 'corporativo' && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#7c3aed' }}>
                            <FiUser size={12} /> Corporativo
                          </span>
                        )}
                        {(pd.sede_nombre || pd.codigo_sede) && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiMapPin size={12} /> {pd.sede_nombre || pd.codigo_sede}</span>
                        )}
                        {pd.sucursal_nombre && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiMapPin size={12} /> Sucursal: {pd.sucursal_nombre}</span>
                        )}
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiPackage size={12} /> {itemsCount} items</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiCalendar size={12} /> {formatearFecha(pd.fechapedido)}</span>
                        {pd.delivery_nombre && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiTruck size={12} /> {pd.delivery_nombre}</span>
                        )}
                      </div>

                      {pd.metodo_pago && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--agx-muted)', marginTop: 5 }}>
                          <FiCreditCard size={11} /> {pd.metodo_pago}
                          {pd.comprobante_url && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--agx-pri2)', fontWeight: 600 }}>
                              · <FiImage size={11} /> comprobante
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 4px' }}>
                <button onClick={cargarMas} disabled={loadingMore} style={{ padding: '10px 22px', borderRadius: 12, border: `1.5px solid var(--agx-pri2)`, background: 'transparent', color: 'var(--agx-pri2)', cursor: loadingMore ? 'wait' : 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>
                  {loadingMore ? 'Cargando...' : 'Cargar más'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detalle */}
      {detalle && (
        <div onClick={(e) => e.target === e.currentTarget && setDetalle(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--agx-surface)', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            {(() => {
              const meta = ESTADOS_META[detalle.estado] || { label: detalle.estado, color: '#9e9e9e' };
              return (
                <div style={{ padding: '16px 18px', background: `linear-gradient(135deg, ${meta.color}, ${meta.color}dd)`, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={() => setDetalle(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FiArrowLeft size={18} />
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>Pedido #{detalle.id}</div>
                    <div style={{ fontSize: 12, opacity: 0.92, marginTop: 2 }}>{formatearFecha(detalle.fechapedido)}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>TOTAL</div>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{fmt(parseFloat(detalle.total || 0) + parseFloat(detalle.costo_envio || 0))}</div>
                  </div>
                </div>
              );
            })()}

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
              {detalleLoading ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--agx-muted)' }}>Cargando detalle...</div>
              ) : (
                <>
                  {/* General */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                    {(() => {
                      const tipo = TIPO_ENTREGA[detalle.tipo_entrega] || TIPO_ENTREGA.delivery;
                      const TipoIcon = tipo.icon;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--agx-text)' }}>
                          <TipoIcon size={15} color={tipo.color} />
                          <span style={{ fontWeight: 600 }}>{tipo.label}</span>
                        </div>
                      );
                    })()}
                    {detalle.cliente && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--agx-text)' }}><FiUser size={15} color="var(--agx-muted)" /><span>{detalle.cliente}</span></div>
                    )}
                    {detalle.telefono && (
                      <div style={{ fontSize: 12, color: 'var(--agx-muted)', paddingLeft: 23 }}>{detalle.telefono}</div>
                    )}
                    {(detalle.sede_nombre || detalle.codigo_sede) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--agx-text)' }}><FiMapPin size={15} color="var(--agx-muted)" /><span>{detalle.sede_nombre || detalle.codigo_sede}</span></div>
                    )}
                    {detalle.delivery_nombre && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--agx-text)' }}><FiTruck size={15} color="var(--agx-muted)" /><span>Repartidor: {detalle.delivery_nombre}</span></div>
                    )}
                    {detalle.direccion_entrega && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--agx-text)' }}><FiMapPin size={15} color="var(--agx-muted)" /><span>{detalle.direccion_entrega}</span></div>
                    )}
                  </div>

                  {/* Totales */}
                  <div style={{ background: 'var(--agx-bg)', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: 'var(--agx-muted)' }}><span>Subtotal</span><span>{fmt(detalle.total)}</span></div>
                    {parseFloat(detalle.costo_envio || 0) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: 'var(--agx-muted)' }}><span>Costo de envío</span><span>{fmt(detalle.costo_envio)}</span></div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, padding: '4px 0 0', borderTop: '1px solid var(--agx-border)', marginTop: 4, color: 'var(--agx-text)' }}><span>Total</span><span>{fmt(parseFloat(detalle.total || 0) + parseFloat(detalle.costo_envio || 0))}</span></div>
                  </div>

                  {/* Productos */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-muted)', marginBottom: 4 }}>Productos ({contarItems(detalle)})</div>
                  {renderProductos(detalle.productos)}

                  {/* Pago */}
                  {(detalle.metodo_pago || detalle.datos_pago) && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-muted)', marginTop: 14, marginBottom: 6 }}>Pago</div>
                      <div style={{ background: 'var(--agx-green-bg, #e6f4ea)', borderRadius: 12, padding: '10px 12px' }}>
                        {(() => {
                          const dp = parseDatosPago(detalle.datos_pago);
                          const metodoNombre = dp?.metodo_nombre || detalle.metodo_pago || '—';
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--agx-text)' }}>
                              <FiCreditCard size={15} /><span style={{ fontWeight: 600 }}>{metodoNombre}</span>
                            </div>
                          );
                        })()}
                        {(() => {
                          const dp = parseDatosPago(detalle.datos_pago);
                          if (dp?.datos_cliente && typeof dp.datos_cliente === 'object') {
                            return Object.entries(dp.datos_cliente).map(([k, v]) => (
                              <div key={k} style={{ fontSize: 12, color: 'var(--agx-muted)', marginTop: 3 }}><strong>{k}:</strong> {String(v)}</div>
                            ));
                          }
                          return null;
                        })()}
                      </div>
                    </>
                  )}

                  {/* Comprobantes de pago (pagos divididos + legacy) */}
                  {(() => {
                    const pagosArr = Array.isArray(detalle.pagos) ? detalle.pagos : [];
                    const conComprobante = pagosArr.filter((p) => {
                      const comps = Array.isArray(p.comprobantes) && p.comprobantes.length > 0
                        ? p.comprobantes
                        : p.comprobante_url ? [{ url: p.comprobante_url }] : [];
                      return comps.length > 0;
                    });
                    if (conComprobante.length === 0 && !detalle.comprobante_url) return null;
                    return (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-muted)', marginBottom: 6 }}>Comprobantes de pago</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {conComprobante.map((p) => {
                            const comps = Array.isArray(p.comprobantes) && p.comprobantes.length > 0
                              ? p.comprobantes
                              : p.comprobante_url ? [{ url: p.comprobante_url }] : [];
                            const aprobado = p.estado === 'aprobado';
                            const rechazado = p.estado === 'rechazado';
                            return (
                              <div key={p.id} style={{ background: 'var(--agx-bg2, #f3f4f6)', borderRadius: 10, padding: '10px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--agx-text)' }}>
                                    {p.metodo_nombre || 'Método de pago'} · {fmt(p.monto)}
                                  </span>
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: aprobado ? 'var(--agx-green-bg, #e6f4ea)' : rechazado ? 'var(--agx-red-bg, #fef2f2)' : 'var(--agx-amber-bg, #fef7e0)', color: aprobado ? 'var(--agx-green, #0f9d58)' : rechazado ? 'var(--agx-danger-text, #991b1b)' : 'var(--agx-warning-text, #b06000)' }}>
                                    {aprobado ? 'Aprobado' : rechazado ? 'Rechazado' : 'Pendiente'}
                                  </span>
                                </div>
                                {p.referencia && (
                                  <div style={{ fontSize: 12, color: 'var(--agx-muted)', marginTop: 2 }}>Ref: {p.referencia}</div>
                                )}
                                {p.notas && (
                                  <div style={{ fontSize: 11, color: 'var(--agx-muted)', marginTop: 2 }}>{p.notas}</div>
                                )}
                                {comps.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                    {comps.map((c, ci) => (
                                      <button
                                        key={ci}
                                        onClick={() => setComprobante({ url: c.url, titulo: `Comprobante de ${p.metodo_nombre || 'pago'}${comps.length > 1 ? ` (${ci + 1})` : ''}` })}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--agx-border)', background: 'var(--agx-surface)', color: 'var(--agx-pri2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                                      >
                                        <FiImage size={12} /> Ver comprobante{comps.length > 1 ? ` ${ci + 1}` : ''}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {pagosArr.length === 0 && detalle.comprobante_url && (
                            <button
                              onClick={() => setComprobante({ url: detalle.comprobante_url, titulo: `Comprobante #${detalle.id}` })}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--agx-border)', background: 'var(--agx-surface)', color: 'var(--agx-pri2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                              <FiImage size={13} /> Ver comprobante de pago
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Timeline */}
                  {Array.isArray(detalle._historial) && detalle._historial.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--agx-muted)', marginTop: 16, marginBottom: 8 }}>Línea de tiempo</div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {detalle._historial.map((h, i) => {
                          const meta = ESTADOS_META[h.estado_nuevo] || { label: h.estado_nuevo, color: '#9e9e9e' };
                          return (
                            <div key={h.id || i} style={{ display: 'flex', gap: 10 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: meta.color, marginTop: 3 }} />
                                {i < detalle._historial.length - 1 && <span style={{ width: 2, flex: 1, backgroundColor: 'var(--agx-border)', marginTop: 2 }} />}
                              </div>
                              <div style={{ paddingBottom: 14, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--agx-text)' }}>
                                  {meta.label}
                                  {h.nota ? <span style={{ fontWeight: 400, color: 'var(--agx-muted)' }}> — {h.nota}</span> : null}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--agx-faint)', marginTop: 1 }}>
                                  {formatearFecha(h.creado_en)}
                                  {h.actor_nombre ? ` · ${h.actor_nombre}` : h.actor_cedula ? ` · ${h.actor_cedula}` : ''}
                                  {h.actor_rol ? ` (${h.actor_rol})` : ''}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Anular */}
                  {puedeAnular && !['cancelado', 'credito_pagado', 'rechazado'].includes(detalle.estado) && (
                    <div style={{ marginTop: 16 }}>
                      <button onClick={() => anular(detalle)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: 11, borderRadius: 10, border: '1px solid var(--agx-danger-text, #991b1b)', background: 'var(--agx-danger-bg, #fef2f2)', color: 'var(--agx-danger-text, #991b1b)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <FiXCircle size={15} /> Anular pedido
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comprobante */}
      {comprobante && (
        <div onClick={(e) => e.target === e.currentTarget && setComprobante(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--agx-surface)', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--agx-border)' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--agx-text)' }}>{comprobante.titulo || `Comprobante #${comprobante.id}`}</span>
              <button onClick={() => setComprobante(null)} style={{ border: 'none', background: 'var(--agx-bg)', borderRadius: 8, cursor: 'pointer', padding: 8, color: 'var(--agx-muted)', display: 'flex' }}>
                <FiX size={16} />
              </button>
            </div>
            <img src={getImagenUrl(comprobante.url || comprobante.comprobante_url)} alt="Comprobante" style={{ width: '100%', display: 'block', objectFit: 'contain' }} />
          </div>
        </div>
      )}

      {/* Confirmación */}
      {confirmar && (
        <div onClick={(e) => e.target === e.currentTarget && setConfirmar(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--agx-surface)', borderRadius: 16, width: '100%', maxWidth: 380, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--agx-text)', marginBottom: 8 }}>{confirmar.title}</div>
            <div style={{ fontSize: 13, color: 'var(--agx-muted)', marginBottom: 16 }}>{confirmar.message}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirmar(null)} style={{ flex: 1, padding: 11, borderRadius: 10, border: '1px solid var(--agx-border)', background: 'var(--agx-surface)', color: 'var(--agx-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancelar</button>
              <button onClick={confirmar.onConfirm} style={{ flex: 1, padding: 11, borderRadius: 10, border: 'none', background: '#ea4335', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Anular</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PedidosHistorial;
